type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null

const hasString = <K extends string>(
  value: unknown,
  key: K
): value is Record<K, string> =>
  isRecord(value) && typeof value[key] === 'string'

export type AuthenticatedTokens = {
  access: string
  refresh: string
}

export type OnboardingRequired = {
  onboarding_token: string
}

export const asAuthenticatedTokens = (
  value: unknown
): AuthenticatedTokens | null => {
  if (hasString(value, 'access') && hasString(value, 'refresh')) {
    return { access: value.access, refresh: value.refresh }
  }
  return null
}

export const asOnboardingRequired = (
  value: unknown
): OnboardingRequired | null => {
  if (hasString(value, 'onboarding_token')) {
    return { onboarding_token: value.onboarding_token }
  }
  return null
}

export const isMfaRequired = (value: unknown): boolean =>
  isRecord(value) && value.mfa_required === true

export type MfaRequiredResponse = {
  mfa_access_token: string
  // True when the member is compelled by policy but hasn't set up a second
  // factor yet — the client should frame the OTP step as enrollment.
  mfa_setup_required: boolean
}

export const asMfaRequired = (value: unknown): MfaRequiredResponse | null => {
  if (
    isRecord(value) &&
    value.mfa_required === true &&
    hasString(value, 'mfa_access_token')
  ) {
    return {
      mfa_access_token: value.mfa_access_token,
      mfa_setup_required: (value as UnknownRecord).mfa_setup_required === true
    }
  }
  return null
}

export const getAuthUrl = (value: unknown): string | null => {
  if (hasString(value, 'auth_url')) return value.auth_url
  return null
}

export const getReturnOrigin = (value: unknown): string | null => {
  if (hasString(value, 'return_origin')) return value.return_origin
  return null
}
